import { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, useMap, useMapEvents } from "react-leaflet";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Thermometer, Plant, MapPin, Crosshair, Broadcast, Stack, Polygon as PolyIcon, Info, CursorClick, Play, Pause } from "@phosphor-icons/react";
import { getEeLayer, getMunicipio, getZones, postRegionStats, getEePointInfo } from "@/lib/api";
import PointSeriesChart from "@/components/rs/PointSeriesChart";
import { Sheet, SheetToggles } from "@/components/rs/mobileSheet";

const BASEMAPS = {
  satelite: { label: "Satélite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Teselas &copy; <a href='https://www.esri.com' target='_blank'>Esri</a> — Fuente: Esri, Maxar, Earthstar Geographics, GIS User Community", cls: "" },
  gris: { label: "Gris", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; <a href='https://www.openstreetmap.org/copyright' target='_blank'>OpenStreetMap</a> contributors (ODbL)", cls: "cm-gray" },
  claro: { label: "Claro", url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png", attribution: "&copy; <a href='https://www.openstreetmap.org/copyright' target='_blank'>OpenStreetMap</a> contributors, &copy; <a href='https://carto.com/attributions' target='_blank'>CARTO</a>", cls: "" },
};

const YEARS = Array.from({ length: 25 }, (_, i) => 2000 + i);

function FlyTo({ coord }) {
  const map = useMap();
  useEffect(() => { if (coord) map.flyTo([coord.lat, coord.lon], 13, { duration: 1.1 }); }, [coord, map]);
  return coord ? (
    <CircleMarker center={[coord.lat, coord.lon]} radius={9}
      pathOptions={{ color: "#fff", weight: 2, fillColor: "#f59e0b", fillOpacity: 0.9 }}>
      <Popup><div className="mono" style={{ fontSize: 12 }}>{coord.lat.toFixed(5)}, {coord.lon.toFixed(5)}</div></Popup>
    </CircleMarker>
  ) : null;
}

function ClickInspect({ year, source, onPoint }) {
  useMapEvents({
    click(e) {
      if (e.originalEvent?.target?.closest?.(".leaflet-pm-toolbar")) return;
      const { lat, lng } = e.latlng;
      onPoint({ lat, lon: lng, loading: true });
      getEePointInfo(lat, lng, year, source)
        .then((d) => onPoint({ ...d, loading: false }))
        .catch(() => onPoint(null));
    },
  });
  return null;
}

function DrawTools({ onCreate }) {
  const map = useMap();
  useEffect(() => {
    if (!map.pm) return;
    map.pm.addControls({ position: "topright", drawMarker: false, drawPolyline: false,
      drawCircle: false, drawCircleMarker: false, drawText: false, drawRectangle: true,
      drawPolygon: true, editMode: false, dragMode: false, cutPolygon: false,
      rotateMode: false, removalMode: false });
    try { map.pm.setLang("es"); } catch (e) {}
    let last = null;
    const handler = (e) => {
      if (last) { try { last.remove(); } catch (x) {} }
      last = e.layer;
      onCreate(e.layer.toGeoJSON().geometry);
    };
    map.on("pm:create", handler);
    return () => { map.off("pm:create", handler); try { map.pm.removeControls(); } catch (e) {} };
  }, [map, onCreate]);
  return null;
}

export default function ThermalView({
  index = "lst",
  unit = "°C",
  title = "Temperatura de superficie",
  subtitle = "LST real (Landsat 30 m) recortada a todo el municipio de Constanza.",
  landsatRes = "30 m",
  tid = "thermal",
}) {
  const source = "landsat";
  const [year, setYear] = useState(2024);
  const [layer, setLayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [muni, setMuni] = useState(null);
  const [zones, setZones] = useState([]);
  const [latIn, setLatIn] = useState("18.9092");
  const [lonIn, setLonIn] = useState("-70.7396");
  const [coord, setCoord] = useState(null);
  const [region, setRegion] = useState(null);
  const [point, setPoint] = useState(null);
  const [basemap, setBasemap] = useState("satelite");
  const [sheet, setSheet] = useState(null);
  const [playing, setPlaying] = useState(false);
  const cacheRef = useRef({}); // capas ya cargadas en esta sesión (no se vuelven a pedir ni desaparecen)
  const idx = YEARS.indexOf(year);
  const isLst = index === "lst";
  const Icon = isLst ? Thermometer : Plant;

  useEffect(() => {
    getMunicipio().then(setMuni).catch(() => {});
    getZones().then(setZones).catch(() => {});
  }, []);

  const load = useCallback((y, src) => {
    const k = `${index}:${src}:${y}`;
    const hit = cacheRef.current[k];
    // Ya cargado en esta sesión y con token vigente (<30 min): se muestra al instante, sin recargar.
    if (hit && Date.now() - hit.t < 30 * 60 * 1000) {
      setLayer(hit.data); setLoading(false); return;
    }
    setLoading(true); // NO se limpia la capa anterior: los datos no desaparecen mientras carga la nueva
    getEeLayer(y, index, src)
      .then((d) => {
        cacheRef.current[k] = { data: d, t: Date.now() };
        setLayer(d);
        if (d.scenes === 0) toast.warning(`Sin datos para ${y}`);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Error de Earth Engine"))
      .finally(() => setLoading(false));
  }, [index]);
  useEffect(() => { load(year, source); }, [year, source, load]);

  // Animación del avance térmico/vegetal 2000→2024 (bucle). Los años se cachean en el backend.
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setYear((y) => YEARS[(YEARS.indexOf(y) + 1) % YEARS.length]);
    }, 1500);
    return () => clearInterval(id);
  }, [playing]);

  const goCoord = () => {
    const la = parseFloat(latIn), lo = parseFloat(lonIn);
    if (isNaN(la) || isNaN(lo)) { toast.error("Coordenadas inválidas"); return; }
    setCoord({ lat: la, lon: lo });
  };

  const onDraw = useCallback((geometry) => {
    setRegion({ loading: true });
    postRegionStats(geometry, year, index, source)
      .then(setRegion).catch(() => { setRegion(null); toast.error("Error al medir la región"); });
  }, [year, source, index]);

  const vis = layer?.vis;
  const stats = layer?.stats;
  const legendLabel = isLst ? "Temp. superficie (°C)" : "NDVI (vigor vegetal)";
  const acc = isLst ? "#f97316" : "#22c55e";

  return (
    <div className="flex-1 flex min-h-0">
      <Sheet open={sheet === "controls"} side="left" testid={`${tid}-sidebar`} className="lg:w-[300px] space-y-5">
        <div>
          <div className="plex text-sm font-medium mb-1 flex items-center gap-1.5"><Icon size={15} /> {title}</div>
          <p className="text-[11px] text-[#475569] leading-relaxed">{subtitle}</p>
        </div>

        <section>
          <div className="label-caps mb-2 flex items-center justify-between">
            <span>Año</span><span className="mono text-[#3f8e33] text-lg">{year}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPlaying((p) => !p)} data-testid={`${tid}-play`}
              title={playing ? "Pausar animación" : "Animar 2000 → 2024"}
              className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md bg-[#4a9b3c] hover:bg-[#3f8e33] text-white transition-colors">
              {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
            </button>
            <div className="flex-1">
              <Slider value={[idx]} min={0} max={YEARS.length - 1} step={1}
                onValueChange={([v]) => { setPlaying(false); setYear(YEARS[v]); }} data-testid={`${tid}-year-slider`} />
              <div className="flex justify-between mono text-[10px] text-[#475569] mt-1.5"><span>2000</span><span>2024</span></div>
            </div>
          </div>
        </section>

        <section>
          <div className="label-caps mb-2 flex items-center gap-1.5"><Crosshair size={13} /> Ir a coordenada</div>
          <div className="grid grid-cols-2 gap-2">
            <input value={latIn} onChange={(e) => setLatIn(e.target.value)} data-testid="coord-lat"
              className="bg-[#f8faf9] border border-[#e5e7eb] rounded-md px-2 py-1.5 text-sm mono text-[#1f2937] outline-none focus:border-[#4a9b3c]" />
            <input value={lonIn} onChange={(e) => setLonIn(e.target.value)} data-testid="coord-lon"
              className="bg-[#f8faf9] border border-[#e5e7eb] rounded-md px-2 py-1.5 text-sm mono text-[#1f2937] outline-none focus:border-[#4a9b3c]" />
          </div>
          <button onClick={goCoord} data-testid="coord-go"
            className="mt-2 w-full flex items-center justify-center gap-2 text-sm py-2 rounded-md bg-[#4a9b3c] hover:bg-[#3f8e33] text-white transition-colors">
            <MapPin size={14} weight="bold" /> Ubicar
          </button>
        </section>

        <section>
          <div className="label-caps mb-2 flex items-center gap-1.5"><Stack size={13} /> Mapa base</div>
          <div className="grid grid-cols-3 gap-1.5">
            {Object.entries(BASEMAPS).map(([k, b]) => (
              <button key={k} onClick={() => setBasemap(k)} data-testid={`${tid}-basemap-${k}`}
                className={`text-[11px] py-1.5 rounded-md border transition-colors ${basemap === k ? "border-[#4a9b3c] bg-[#eef7ed] text-[#2d6624]" : "border-[#e5e7eb] text-[#334155] hover:bg-[#eef7ed]"}`}>
                {b.label}
              </button>
            ))}
          </div>
        </section>

        <section>
          <div className="label-caps mb-2 flex items-center gap-1.5"><PolyIcon size={13} /> Medir un área</div>
          <p className="text-[11px] text-[#475569] leading-relaxed">
            Dibuja un polígono (por puntos) o un rectángulo con las herramientas del mapa para medir
            el valor medio de esa área.
          </p>
        </section>
      </Sheet>

      <main className="flex-1 relative min-w-0" data-testid={`${tid}-map-area`}>
        {loading && (
          <div className="absolute inset-0 z-[600] flex items-center justify-center bg-[#f8faf9]/75 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <Broadcast size={26} className="text-[#3f8e33] animate-pulse" />
              <span className="text-xs mono text-[#334155]">Procesando en Earth Engine…</span>
            </div>
          </div>
        )}
        {muni && (
          <div data-testid={`${tid}-map`} style={{ height: "100%", width: "100%" }}>
          <MapContainer center={[muni.center.lat, muni.center.lon]} zoom={11} className="map-raw"
            bounds={[[muni.bbox.lat_min, muni.bbox.lon_min], [muni.bbox.lat_max, muni.bbox.lon_max]]}
            style={{ height: "100%", width: "100%" }}>
            <TileLayer key={basemap} url={BASEMAPS[basemap].url} subdomains="abc"
              className={BASEMAPS[basemap].cls} attribution={BASEMAPS[basemap].attribution} maxZoom={18} />
            {layer?.tile_url && <TileLayer key={layer.tile_url} url={layer.tile_url} opacity={0.85}
              attribution="Datos: Landsat C2 L2 — USGS/NASA vía Google Earth Engine" />}
            <GeoJSON data={muni.geojson} style={{ color: "#22d3ee", weight: 2, fill: false, dashArray: "6 4" }} />
            {zones.map((z) => <GeoJSON key={z.id} data={z.geometry} style={{ color: "#f8fafc", weight: 1.2, fill: false }} />)}
            <DrawTools onCreate={onDraw} />
            <FlyTo coord={coord} />
            <ClickInspect year={year} source={source} onPoint={setPoint} />
            {point && (
              <>
                <CircleMarker center={[point.lat, point.lon]} radius={7}
                  pathOptions={{ color: "#fff", weight: 2, fillColor: "#a855f7", fillOpacity: 0.9 }} />
                <Popup position={[point.lat, point.lon]} key={`${point.lat}-${point.lon}-${point.loading}`}>
                  <div className="mono" style={{ fontSize: 12, lineHeight: 1.6 }} data-testid={`${tid}-point-popup`}>
                    {point.loading ? "Consultando…" : (
                      <>
                        <div>{point.lat.toFixed(5)}, {point.lon.toFixed(5)}</div>
                        {point.inside === false
                          ? <div style={{ color: "#f59e0b" }}>Fuera del municipio</div>
                          : <>
                              <div>LST: <b>{point.lst != null ? `${point.lst} °C` : "—"}</b></div>
                              <div>NDVI: <b>{point.ndvi != null ? point.ndvi : "—"}</b></div>
                            </>}
                      </>
                    )}
                  </div>
                </Popup>
              </>
            )}
          </MapContainer>
          </div>
        )}
        {vis && (
          <div className="absolute bottom-5 left-5 z-[500] glass rounded-md px-4 py-3 w-[250px]" data-testid={`${tid}-legend`}>
            <div className="label-caps mb-2">{legendLabel} · Landsat {landsatRes}</div>
            <div className="h-2.5 w-full rounded-full" style={{ background: `linear-gradient(90deg, ${vis.palette.join(",")})` }} />
            <div className="flex justify-between mt-1.5 mono text-[11px] text-[#1f2937]">
              <span>{vis.min}</span><span>{((vis.min + vis.max) / 2).toFixed(isLst ? 0 : 2)}</span><span>{vis.max}</span>
            </div>
          </div>
        )}
        <div className="absolute top-4 left-[56px] z-[500] glass rounded-md px-3 py-1.5 text-[11px] mono text-[#1f2937] whitespace-nowrap">
          {layer?.sensor || "EE"} · {year}
        </div>
        <div className="absolute top-4 right-4 z-[500] glass rounded-md px-2.5 py-1.5 text-[10px] text-[#334155] flex items-center gap-1.5 whitespace-nowrap">
          <CursorClick size={13} className="text-purple-400" /> Clic: LST + NDVI
        </div>
        {layer?.meta && (
          <div className="absolute bottom-5 right-5 z-[500] glass rounded-md px-3.5 py-3 w-[260px]" data-testid={`${tid}-meta`}>
            <div className="label-caps mb-2 flex items-center gap-1.5"><Info size={13} /> Información de la capa</div>
            <dl className="text-[11px] text-[#1f2937] space-y-1">
              <MetaRow k="Fuente" v={layer.meta.fuente} />
              <MetaRow k="Colección" v={layer.meta.coleccion} />
              <MetaRow k="Sensor" v={layer.meta.sensor} />
              <MetaRow k="Período" v={layer.meta.periodo} />
              <MetaRow k="Unidad" v={layer.meta.unidad} />
              <MetaRow k="Resolución" v={layer.meta.resolucion} />
              <MetaRow k="Procesamiento" v={layer.meta.procesamiento} />
            </dl>
          </div>
        )}
      </main>

      <Sheet open={sheet === "data"} side="right" testid={`${tid}-stats`} className="lg:w-[320px] space-y-4">
        <div className="plex text-sm font-medium">{isLst ? "Temperatura (municipio)" : "NDVI (municipio)"}</div>
        {stats ? (
          <>
            <div className="grid grid-cols-3 gap-2">
              <Mini label="Media" value={stats.mean} unit={unit} accent={acc} testId={`${tid}-mean`} />
              <Mini label="Mín" value={stats.min} unit={unit} accent="#38bdf8" />
              <Mini label="Máx" value={stats.max} unit={unit} accent="#ef4444" />
            </div>
            <div className="flex items-center gap-2 text-[11px] text-[#334155]">
              <Stack size={13} /> {layer.scenes} escenas · {layer.sensor}
            </div>
          </>
        ) : <div className="text-[12px] text-[#475569]">{loading ? "Calculando…" : "Sin datos."}</div>}

        <div className="pt-2 border-t border-[#e5e7eb]">
          <div className="label-caps mb-2 flex items-center gap-1.5"><PolyIcon size={13} /> Área dibujada</div>
          {!region && <div className="text-[11px] text-[#64748b]">Dibuja un polígono o rectángulo para medirlo.</div>}
          {region?.loading && <div className="text-[11px] text-[#334155] animate-pulse">Midiendo área…</div>}
          {region && !region.loading && (
            <div className="grid grid-cols-3 gap-2" data-testid="region-stats">
              <Mini label="Media" value={region.mean} unit={unit} accent={acc} testId="region-mean" />
              <Mini label="Mín" value={region.min} unit={unit} accent="#38bdf8" />
              <Mini label="Máx" value={region.max} unit={unit} accent="#ef4444" />
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-[#e5e7eb]">
          <div className="label-caps mb-2 flex items-center gap-1.5"><CursorClick size={13} /> Punto inspeccionado</div>
          {!point && <div className="text-[11px] text-[#64748b]">Haz clic en el mapa para ver las coordenadas, la LST y el NDVI del punto.</div>}
          {point?.loading && <div className="text-[11px] text-[#334155] animate-pulse">Consultando Earth Engine…</div>}
          {point && !point.loading && (
            <div data-testid={`${tid}-point-panel`} className="space-y-2">
              <div className="mono text-[11px] text-[#1f2937]">{point.lat.toFixed(5)}, {point.lon.toFixed(5)}</div>
              {point.inside === false ? (
                <div className="text-[11px] text-amber-500">El punto está fuera del municipio de Constanza.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Mini label="LST" value={point.lst} unit="°C" accent="#f97316" testId={`${tid}-point-lst`} />
                  <Mini label="NDVI" value={point.ndvi} unit="" accent="#22c55e" testId={`${tid}-point-ndvi`} />
                </div>
              )}
              {point.inside !== false && (
                <PointSeriesChart lat={point.lat} lon={point.lon} source={source} testId={`${tid}-point-series`} />
              )}
            </div>
          )}
        </div>

        <div className="text-[11px] text-[#475569] leading-relaxed pt-2 border-t border-[#e5e7eb]">
          Fuente Landsat C2 L2 ({landsatRes}) vía Google
          Earth Engine, temporada seca (dic–mar), recortado al municipio (850 km²).
        </div>
      </Sheet>
      <SheetToggles sheet={sheet} setSheet={setSheet} />
    </div>
  );
}

function Mini({ label, value, unit = "", accent, testId }) {
  return (
    <div className="border border-[#e5e7eb] p-2.5 bg-[#f8faf9]" data-testid={testId}>
      <div className="label-caps">{label}</div>
      <div className="mono text-base font-semibold" style={{ color: accent }}>
        {value != null ? value : "—"}<span className="text-[10px] text-[#475569] ml-0.5">{unit}</span>
      </div>
    </div>
  );
}

function MetaRow({ k, v }) {
  return (
    <div className="flex gap-2">
      <dt className="text-[#475569] shrink-0" style={{ minWidth: 78 }}>{k}</dt>
      <dd className="text-[#1f2937]">{v}</dd>
    </div>
  );
}
